import moment from 'moment';
import { CompositeModel } from './composite.model';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { NotificationModel } from '../low-level/notification.model';
import { TimeModel } from '../primitive/time.model';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { ConfigConstants } from '../../config/config-constants';
import { TextModel } from '../primitive/text.model';

export class ApprovedPCHDurationModel extends CompositeModel {
    public startDateTime: TimeModel;
    public endDateTime: TimeModel;
    public approver: TextModel;
    public approvedPCHDuration: ApprovedPCHDateTimeRange;
    public noPCHAvailable = false;
    public pchDateTimeRange: PCHDateTimeRangeModel;
    public tomorrowStartPCHDateTime: moment.Moment | null = null;
    public approvedPCH: PCHDateTimeRangeModel;

    private static readonly CODE_GROUP = ConfigConstants.ApprovedPCHCodes.CODE_GROUP;
    private static readonly CODE_NAME = ConfigConstants.ApprovedPCHCodes.CODE_NAME;

    /**
     * Approved PCH model for updating PCH duration.
     *
     * @param notification current notification data
     */
    public constructor(private readonly notification: NotificationModel) {
        super('ApprovedPCHDurationModel');
        this.initializePCHDateTimes();
        this.pchDateTimeRange = this.getTodayPCHTimeRange();

        this.approver = <TextModel>this.set(new TextModel('Approver'));
        this.approver.options = {
            ...this.approver.options,
            required: true,
        };
    }

    private initializePCHDateTimes(): void {
        const entry = this.notification.codeSet.find(
            (code) =>
                code.codeGroup.value === ApprovedPCHDurationModel.CODE_GROUP &&
                code.codeName.value === ApprovedPCHDurationModel.CODE_NAME,
        );

        if (!entry) {
            this.noPCHAvailable = true;
            const now = moment();
            this.assignDateTime(now.clone(), now.clone());
            return;
        }

        const [startDateString, endDateString] = entry.codeDesc.value.split(';');
        const start = this.parseDateTime(startDateString);
        const end = this.parseDateTime(endDateString);

        this.assignDateTime(start, end);
        this.updateApprovedPCHDuration();
    }

    private parseDateTime(value: string): moment.Moment {
        const parsed = moment(value, ConfigConstants.formatPatternDateTimePCH);
        return parsed.isValid() ? parsed : moment();
    }

    private assignDateTime(start: moment.Moment, end: moment.Moment): void {
        this.startDateTime = <TimeModel>this.set(new TimeModel('StartDate', start));
        this.endDateTime = <TimeModel>this.set(new TimeModel('EndDate', end));
    }

    public setDateTimeOptions(): void {
        if (!this.startDateTime?.value) {
            return;
        }

        this.endDateTime = assignModelOptions(
            this.endDateTime,
            { ...this.endDateTime.options, minValue: this.startDateTime.value.clone() },
            this,
        );
    }

    public updateApprovedPCHDuration(): void {
        this.noPCHAvailable = false;
        this.approvedPCHDuration = {
            start: this.startDateTime.value.format(ConfigConstants.formatPatternDateTimeWithSeparator),
            end: this.endDateTime.value.format(ConfigConstants.formatPatternDateTimeWithSeparator),
        };
        this.approvedPCH = {
            start: this.startDateTime.value,
            end: this.endDateTime.value,
        };
    }

    public getOutModel(): ODataApprovedPCHDurationOutModel {
        const notificationNo = this.notification.identifier.getQmnum();
        const taskNo = this.notification.identifier.getManum();

        return {
            MessageType: ConfigConstants.ODataPCHDurationTypeEnum.PCHW,
            Notification: notificationNo,
            Task: taskNo,
            FrontId: this.notification.identifier.getFrontId(),

            StartDate: this.startDateTime.value.format(ConfigConstants.formatSAPDate),
            StartTime: this.startDateTime.value.format(ConfigConstants.formatXSDTimeDuration),
            EndDate: this.endDateTime.value.format(ConfigConstants.formatSAPDate),
            EndTime: this.endDateTime.value.format(ConfigConstants.formatXSDTimeDuration),

            LongTextInfo: [
                {
                    Notification: notificationNo,
                    Task: taskNo,
                    Tdid: ConfigConstants.ODataPCHDurationTypeEnum.APPR,
                    Tdline: this.approver.value,
                },
            ],
        };
    }

    public getTodayPCHTimeRange(): PCHDateTimeRangeModel {
        const today = moment();
        const tomorrow = moment().add(1, 'days');
        const todayDay = today.format('dddd').toLowerCase(); // monday, tuesday...
        const tomorrowDay = tomorrow.format('dddd').toLowerCase();

        const entries = this.notification.contract.sections.flatMap((section) => section.entries);

        const startEntry = entries.find((entry) =>
            entry.label.toLowerCase().replace(/\s+/g, '').includes(`${todayDay}pchstarttime`),
        );

        const endEntry = entries.find((entry) =>
            entry.label.toLowerCase().replace(/\s+/g, '').includes(`${todayDay}pchendtime`),
        );

        const tomorrowStartEntry = entries.find((entry) =>
            entry.label.toLowerCase().replace(/\s+/g, '').includes(`${tomorrowDay}pchstarttime`),
        );

        this.tomorrowStartPCHDateTime = tomorrowStartEntry
            ? moment(
                  `${tomorrow.format(ConfigConstants.formatPatternDateGeneric)} ${moment(tomorrowStartEntry.value, 'HHmmss').format('HH:mm')}`,
                  ConfigConstants.formatPatternDateTimeGeneric,
              )
            : null;
        // If today's PCH end time is before start time, use tomorrow's PCH times
        // Format ContValue: HHmmss → HH:mm
        const formatValue = (val: string): string => moment(val, 'HHmmss').format('HH:mm');

        const dateString = today.format(ConfigConstants.formatPatternDateGeneric);

        return {
            start: startEntry
                ? moment(`${dateString} ${formatValue(startEntry.value)}`, ConfigConstants.formatPatternDateTimeGeneric)
                : null,
            end: endEntry
                ? moment(`${dateString} ${formatValue(endEntry.value)}`, ConfigConstants.formatPatternDateTimeGeneric)
                : null,
        };
    }

    public getPCHAlertTriggerTime(warningMinutes: number): moment.Moment | null {
        const now = moment();
        const osrStart = now.clone(); // OSR happens now
        const pch = this.pchDateTimeRange;

        if (!pch.start || !pch.end) {
            return null;
        }

        // OSR is INSIDE PCH

        if (osrStart.isBetween(pch.start, pch.end, null, '[)')) {
            if (this.approvedPCH && this.approvedPCH.start && this.approvedPCH.end) {
                // OSR inside approved PCH → warn when ApprovedPCH END is approaching
                if (osrStart.isBetween(this.approvedPCH.start, this.approvedPCH.end, null, '[)')) {
                    return this.triggerTime(now, this.approvedPCH.end, warningMinutes);
                }

                // OSR before approved-start → warn NOW
                if (osrStart.isBefore(this.approvedPCH.start)) {
                    return now.add(5, 'seconds');
                }

                //  OSR after approved-end → warn NOW
                if (osrStart.isAfter(this.approvedPCH.end)) {
                    return now.add(5, 'seconds');
                }
            }

            // No approved PCH → warn NOW
            return now.add(5, 'seconds');
        }

        // OSR BEFORE PCH START

        if (osrStart.isBefore(pch.start)) {
            // Approved exists
            if (this.approvedPCH && this.approvedPCH.start && this.approvedPCH.end) {
                // If PCH.start occurs BEFORE ApprovedPCH.start → warn before PCH start
                if (pch.start.isBefore(this.approvedPCH.start)) {
                    return this.triggerTime(now, pch.start, warningMinutes);
                }

                // If PCH.start is AFTER approved start (PCH is inside Approved window)
                // → warn before ApprovedPCH.end
                if (pch.start.isSameOrAfter(this.approvedPCH.start)) {
                    return this.triggerTime(now, this.approvedPCH.end, warningMinutes);
                }

                return null;
            }

            // No approved → warn before PCH start
            return this.triggerTime(now, pch.start, warningMinutes);
        }

        // OSR AFTER PCH END → next-day PCH

        if (osrStart.isSameOrAfter(pch.end) && this.tomorrowStartPCHDateTime) {
            return this.triggerTime(now, this.tomorrowStartPCHDateTime, warningMinutes);
        }

        return null;
    }

    public getPCHAlertTriggerTimeOnAPCHUpdate(warningMinutes: number): moment.Moment | null {
        const now = moment();
        const pch = this.pchDateTimeRange;

        if (!pch.start || !pch.end) {
            return null;
        }

        if (this.approvedPCH && this.approvedPCH.start && this.approvedPCH.end) {
            // Warn when ApprovedPCH END is approaching
            return this.triggerTime(now, this.approvedPCH.end, warningMinutes);
        }
        return null;
    }

    private triggerTime(now: moment.Moment, time: moment.Moment, warningMinutes: number): moment.Moment | null {
        const trigger = time.clone().subtract(warningMinutes, 'minutes');
        return trigger.isBefore(now) ? now.clone().add(5, 'seconds') : trigger;
    }
}
export interface ODataApprovedPCHDurationOutModel extends ODataObject {
    MessageType: string;
    Notification: string;
    Task: string;
    FrontId: string;
    StartDate: string;
    StartTime: string;
    EndDate: string;
    EndTime: string;
    LongTextInfo: LongtextInfoEntry[];
}

export interface LongtextInfoEntry extends ODataObject {
    Notification: string;
    Task: string;
    Tdid: string;
    Tdline: string;
}

export interface ODataApprovedPCHDurationResponseModel extends ODataObject, ODataApprovedPCHDurationOutModel {
    __metadata: {
        id: string;
        uri: string;
        type: string;
    };
}

export type ApprovedPCHDateTimeRange = {
    start: string;
    end: string;
};

export type PCHDateTimeRangeModel = {
    start: moment.Moment | null;
    end: moment.Moment | null;
};
